import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import { ApiResponse } from '../../../types/api';
import ViewLayout from '../../../components/Common/ViewLayout';
import VehicleAgendamentosView from './VehicleAgendamentosView';
import VehicleChecklistPersonalizadoView from './VehicleChecklistPersonalizadoView';

interface QRCodeData {
  qrcode: string;
  veiculo: {
    id: number;
    placa: string;
    tag: string;
    marca: string;
    modelo: string;
  };
  empresa?: {
    nome: string;
    suporte_telefone: string;
  };
  qr_data: string;
}

interface VehicleDetail {
  id: number;
  placa: string;
  modelo: string;
  tipo: string;
  marca: string;
  ano_fabricacao: string;
  cor: string;
  chassi: string;
  renavam: string;
  quilometragem_atual: number;
  capacidade_tanque: number;
  combustivel: string;
  status: string;
  observacoes: string;
  ultima_inspecao: string;
  proxima_inspecao: string;
  valor_fipe: number;
  valor_seguro: number;
  numero_motor: string;
  potencia: string;
  cilindradas: string;
  peso_bruto: number;
  capacidade_carga: number;
  numero_passageiros: number;
  proprietario: string;
  documento_veiculo: string;
  certificado_registro: string;
  data_aquisicao: string;
  data_venda: string;
  localizacao_atual: string;
  centro_custo: string;
  departamento_responsavel: string;
  foto_veiculo: string;
  criado_em: string;
  atualizado_em: string;
}

const VehicleView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState<VehicleDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'detalhes' | 'checklist' | 'agendamentos' | 'qrcode'>('detalhes');
  const [qrCodeData, setQRCodeData] = useState<QRCodeData | null>(null);
  const [isLoadingQRCode, setIsLoadingQRCode] = useState(false);
  const qrCodeRef = useRef<HTMLDivElement>(null);

  const loadVehicle = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiService.getVehicle(Number(id)) as ApiResponse<any>;
      
      if (response.success && response.data) {
        setVehicle(response.data);
      } else {
        throw new Error(response.message || 'Erro ao carregar veículo');
      }
    } catch (error) {
      console.error('Erro ao carregar veículo:', error);
      setError('Erro ao carregar detalhes do veículo');
      utils.showNotification('Erro ao carregar detalhes do veículo', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadVehicle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadQRCode = async () => {
    if (!id || qrCodeData) return;

    try {
      setIsLoadingQRCode(true);
      const response = await apiService.getVehicleQRCode(Number(id)) as ApiResponse<QRCodeData>;

      if (response.success && response.data) {
        setQRCodeData(response.data);
      } else {
        throw new Error(response.message || 'Erro ao gerar QR Code');
      }
    } catch (error) {
      console.error('Erro ao gerar QR Code:', error);
      utils.showNotification('Erro ao gerar QR Code do veículo', { type: 'error' });
    } finally {
      setIsLoadingQRCode(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'qrcode' && !qrCodeData && !isLoadingQRCode) {
      loadQRCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleDownloadQRCode = () => {
    if (!qrCodeData || !vehicle) return;

    const link = document.createElement('a');
    link.download = `qrcode-veiculo-${vehicle.placa.replace(/[^a-zA-Z0-9]/g, '-')}.png`;
    link.href = qrCodeData.qrcode;
    link.click();
  };

  const handlePrintQRCode = () => {
    if (!qrCodeData || !vehicle) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      utils.showNotification('Não foi possível abrir a janela de impressão. Verifique se pop-ups estão permitidos.', { type: 'error' });
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - ${vehicle.placa}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
            }
            .container {
              text-align: center;
              border: 2px solid #333;
              padding: 30px;
              border-radius: 10px;
              max-width: 400px;
            }
            .empresa-nome {
              font-size: 12px;
              color: #666;
              margin-bottom: 10px;
              text-transform: uppercase;
              letter-spacing: 1px;
            }
            .qr-code {
              margin: 15px 0;
            }
            .qr-code img {
              width: 200px;
              height: 200px;
            }
            .vehicle-info {
              margin-top: 15px;
            }
            .vehicle-info h2 {
              font-size: 20px;
              margin-bottom: 8px;
              color: #333;
            }
            .vehicle-info p {
              font-size: 12px;
              color: #666;
              margin: 4px 0;
            }
            .placa {
              font-size: 24px !important;
              font-weight: bold;
              color: #000 !important;
              background: #f0f0f0;
              padding: 8px 16px;
              border-radius: 5px;
              display: inline-block;
              margin-top: 8px;
            }
            .tag {
              font-size: 14px;
              color: #333;
              margin-top: 5px;
            }
            .suporte {
              margin-top: 15px;
              padding-top: 15px;
              border-top: 1px dashed #ccc;
              font-size: 11px;
              color: #666;
            }
            .suporte strong {
              color: #333;
            }
            @media print {
              body {
                padding: 0;
              }
              .container {
                border: 2px solid #000;
              }
            }
          </style>
        </head>
        <body>
          <div class="container">
            ${qrCodeData.empresa?.nome ? `<p class="empresa-nome">${qrCodeData.empresa.nome}</p>` : ''}
            <div class="vehicle-info">
              <h2>${vehicle.marca} ${vehicle.modelo}</h2>
            </div>
            <div class="qr-code">
              <img src="${qrCodeData.qrcode}" alt="QR Code do Veículo" />
            </div>
            <div class="vehicle-info">
              <p class="placa">${vehicle.placa}</p>
              ${qrCodeData.veiculo.tag ? `<p class="tag">TAG: ${qrCodeData.veiculo.tag}</p>` : ''}
            </div>
            ${qrCodeData.empresa?.suporte_telefone ? `
            <div class="suporte">
              <strong>Suporte:</strong> ${qrCodeData.empresa.suporte_telefone}
            </div>
            ` : ''}
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatKM = (km: number | string | undefined | null): string => {
    if (km === undefined || km === null || km === '') return '-';
    // quilometragem_atual e NUMERIC (string do pg); coage antes de formatar
    return Number(km).toLocaleString('pt-BR');
  };

  // Colunas NUMERIC chegam como string pelo pg; coage para numero antes de formatar
  const formatWeight = (kg: number | string | undefined | null): string => {
    if (kg === undefined || kg === null || kg === '') return '-';
    return `${Number(kg).toLocaleString('pt-BR')} kg`;
  };

  const formatCurrency = (value: number | string | undefined | null): string => {
    if (value === undefined || value === null || value === '') return '-';
    return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'ativo': 'status-active',
      'inativo': 'status-inactive',
      'manutencao': 'status-maintenance'
    };
    return statusMap[status] || 'status-pending';
  };

  const getStatusText = (status: string): string => {
    const statusMap: Record<string, string> = {
      'ativo': 'Ativo',
      'inativo': 'Inativo',
      'manutencao': 'Em Manutenção'
    };
    return statusMap[status] || status;
  };

  const getFuelTypeText = (combustivel: string): string => {
    const fuelMap: Record<string, string> = {
      'diesel': 'Diesel',
      'gasolina': 'Gasolina',
      'etanol': 'Etanol',
      'flex': 'Flex',
      'gnv': 'GNV',
      'eletrico': 'Elétrico'
    };
    return fuelMap[combustivel] || combustivel;
  };

  const getVehicleTypeText = (tipo: string): string => {
    const typeMap: Record<string, string> = {
      'caminhao': 'Caminhão',
      'van': 'Van',
      'carro': 'Carro',
      'motocicleta': 'Moto',
      'onibus': 'Ônibus',
      'maquina': 'Outro'
    };
    return typeMap[tipo] || tipo;
  };

  if (isLoading) {
    return (
      <ViewLayout
        title="Carregando Veículo..."
        isLoading={true}
        onBack={() => navigate('/veiculos')}
      >
        <div></div>
      </ViewLayout>
    );
  }

  if (error || !vehicle) {
    return (
      <ViewLayout
        title="Erro ao Carregar"
        onBack={() => navigate('/veiculos')}
      >
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
            <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#e74c3c', marginBottom: '20px' }}></i>
            <h3>{error || 'Veículo não encontrado'}</h3>
            <p>O veículo solicitado não pôde ser carregado.</p>
            <button className="btn btn-primary" onClick={() => navigate('/veiculos')}>
              Voltar para Lista
            </button>
          </div>
        </div>
      </ViewLayout>
    );
  }

  return (
    <ViewLayout
      title="Detalhes do Veículo"
      subtitle={vehicle.modelo}
      entityId={vehicle.id.toString()}
      onBack={() => navigate('/veiculos')}
    >
      {/* Navegação por Abas */}
      <div className="card">
        <div className="card-header">
          <div className="tab-navigation">
            <button 
              className={`tab-button ${activeTab === 'detalhes' ? 'active' : ''}`}
              onClick={() => setActiveTab('detalhes')}
            >
              <i className="fas fa-info-circle"></i>
              Detalhes do Veículo
            </button>
            <button 
              className={`tab-button ${activeTab === 'checklist' ? 'active' : ''}`}
              onClick={() => setActiveTab('checklist')}
            >
              <i className="fas fa-clipboard-check"></i>
              Checklist Personalizado
            </button>
            <button
              className={`tab-button ${activeTab === 'agendamentos' ? 'active' : ''}`}
              onClick={() => setActiveTab('agendamentos')}
            >
              <i className="fas fa-clock"></i>
              Agendamentos
            </button>
            <button
              className={`tab-button ${activeTab === 'qrcode' ? 'active' : ''}`}
              onClick={() => setActiveTab('qrcode')}
            >
              <i className="fas fa-qrcode"></i>
              QR Code
            </button>
          </div>
        </div>
      </div>

      {/* Conteúdo da Aba Detalhes */}
      {activeTab === 'detalhes' && (
        <div>
          <div className="card">
            <div className="card-body">
              {/* Informações Básicas */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Informações Básicas
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="info-item">
                    <label className="form-label">Placa</label>
                    <span className="info-value">{vehicle.placa}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Marca</label>
                    <span className="info-value">{vehicle.marca}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Modelo</label>
                    <span className="info-value">{vehicle.modelo}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Tipo</label>
                    <span className="info-value">{getVehicleTypeText(vehicle.tipo)}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Ano de Fabricação/Modelo</label>
                    <span className="info-value">{vehicle.ano_fabricacao || '-'}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Cor</label>
                    <span className="info-value">{vehicle.cor || '-'}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Status</label>
                    <span className="info-value">
                      <span className={`status-badge ${getStatusClass(vehicle.status)}`}>
                        {getStatusText(vehicle.status)}
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Documentos e Identificação */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Documentos e Identificação
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="info-item">
                    <label className="form-label">Número do Chassi</label>
                    <span className="info-value">{vehicle.chassi || '-'}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">RENAVAM</label>
                    <span className="info-value">{vehicle.renavam || '-'}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Número do Motor</label>
                    <span className="info-value">{vehicle.numero_motor || '-'}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Proprietário</label>
                    <span className="info-value">{vehicle.proprietario || '-'}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Documento do Veículo</label>
                    <span className="info-value">{vehicle.documento_veiculo || '-'}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Certificado de Registro</label>
                    <span className="info-value">{vehicle.certificado_registro || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Especificações Técnicas */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Especificações Técnicas
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="info-item">
                    <label className="form-label">Quilometragem Atual</label>
                    <span className="info-value">{formatKM(vehicle.quilometragem_atual)} km</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Capacidade do Tanque</label>
                    <span className="info-value">{vehicle.capacidade_tanque ? `${Number(vehicle.capacidade_tanque).toLocaleString('pt-BR')} L` : '-'}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Tipo de Combustível</label>
                    <span className="info-value">{getFuelTypeText(vehicle.combustivel)}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Potência</label>
                    <span className="info-value">{vehicle.potencia || '-'}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Cilindradas</label>
                    <span className="info-value">{vehicle.cilindradas || '-'}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Peso Bruto</label>
                    <span className="info-value">{formatWeight(vehicle.peso_bruto)}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Capacidade de Carga</label>
                    <span className="info-value">{formatWeight(vehicle.capacidade_carga)}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Número de Passageiros</label>
                    <span className="info-value">{vehicle.numero_passageiros || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Valores */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Valores
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="info-item">
                    <label className="form-label">Valor FIPE</label>
                    <span className="info-value">{formatCurrency(vehicle.valor_fipe)}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Valor do Seguro</label>
                    <span className="info-value">{formatCurrency(vehicle.valor_seguro)}</span>
                  </div>
                </div>
              </div>

              {/* Datas Importantes */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Datas Importantes
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="info-item">
                    <label className="form-label">Data de Aquisição</label>
                    <span className="info-value">{formatDate(vehicle.data_aquisicao)}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Data de Venda</label>
                    <span className="info-value">{formatDate(vehicle.data_venda)}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Última Inspeção</label>
                    <span className="info-value">{formatDate(vehicle.ultima_inspecao)}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Próxima Inspeção</label>
                    <span className="info-value">{formatDate(vehicle.proxima_inspecao)}</span>
                  </div>

                </div>
              </div>

              {/* Localização e Contabilidade */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Localização e Contabilidade
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="info-item">
                    <label className="form-label">Localização Atual</label>
                    <span className="info-value">{vehicle.localizacao_atual || '-'}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Centro de Custo</label>
                    <span className="info-value">{vehicle.centro_custo || '-'}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Departamento Responsável</label>
                    <span className="info-value">{vehicle.departamento_responsavel || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Foto do Veículo */}
              {vehicle.foto_veiculo && (
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                    Foto do Veículo
                  </h3>
                  
                  <div style={{ textAlign: 'center' }}>
                    <img 
                      src={vehicle.foto_veiculo} 
                      alt={`Foto do veículo ${vehicle.placa}`}
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '300px', 
                        borderRadius: '8px',
                        border: '1px solid #ddd'
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                    <p style={{ marginTop: '10px', color: '#7f8c8d' }}>
                      <a href={vehicle.foto_veiculo} target="_blank" rel="noopener noreferrer">
                        Ver imagem em tamanho real
                      </a>
                    </p>
                  </div>
                </div>
              )}

              {/* Observações */}
              {vehicle.observacoes && (
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                    Observações
                  </h3>
                  
                  <div className="info-item">
                    <label className="form-label">Observações</label>
                    <p style={{ color: '#7f8c8d', lineHeight: '1.6', margin: 0, padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                      {vehicle.observacoes}
                    </p>
                  </div>
                </div>
              )}

              {/* Informações de Auditoria */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Informações de Auditoria
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="info-item">
                    <label className="form-label">Criado em</label>
                    <span className="info-value">{formatDateTime(vehicle.criado_em)}</span>
                  </div>

                  <div className="info-item">
                    <label className="form-label">Atualizado em</label>
                    <span className="info-value">{formatDateTime(vehicle.atualizado_em)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Ações */}
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button 
              className="btn btn-secondary"
              onClick={() => navigate('/veiculos')}
            >
              <i className="fas fa-list"></i>
              Voltar para Lista
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => navigate(`/veiculos/editar/${vehicle.id}`)}
            >
              <i className="fas fa-edit"></i>
              Editar Veículo
            </button>
          </div>
        </div>
      )}

      {/* Conteúdo da Aba Checklist Personalizado */}
      {activeTab === 'checklist' && (
        <VehicleChecklistPersonalizadoView />
      )}

      {/* Conteúdo da Aba Agendamentos */}
      {activeTab === 'agendamentos' && (
        <VehicleAgendamentosView />
      )}

      {/* Conteúdo da Aba QR Code */}
      {activeTab === 'qrcode' && (
        <div className="card">
          <div className="card-body">
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                QR Code do Veículo
              </h3>
              <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
                Use este QR Code para identificar o veículo. Imprima e fixe no veículo para facilitar a identificação durante os checklists.
              </p>

              {isLoadingQRCode ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#04C64F' }}></i>
                  <p style={{ marginTop: '15px', color: '#7f8c8d' }}>Gerando QR Code...</p>
                </div>
              ) : qrCodeData ? (
                <div ref={qrCodeRef} style={{ textAlign: 'center' }}>
                  <div style={{
                    display: 'inline-block',
                    border: '2px solid #ddd',
                    borderRadius: '10px',
                    padding: '30px',
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                  }}>
                    <img
                      src={qrCodeData.qrcode}
                      alt={`QR Code do veículo ${vehicle?.placa}`}
                      style={{
                        width: '250px',
                        height: '250px',
                        display: 'block',
                        margin: '0 auto'
                      }}
                    />
                    <div style={{ marginTop: '20px' }}>
                      <p style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: '#2c3e50',
                        backgroundColor: '#f8f9fa',
                        padding: '10px 20px',
                        borderRadius: '5px',
                        display: 'inline-block'
                      }}>
                        {vehicle?.placa}
                      </p>
                      <p style={{ marginTop: '10px', color: '#7f8c8d' }}>
                        {vehicle?.marca} {vehicle?.modelo}
                      </p>
                      {qrCodeData.veiculo.tag && (
                        <p style={{ marginTop: '5px', color: '#7f8c8d' }}>
                          TAG: {qrCodeData.veiculo.tag}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Botões de Ação */}
                  <div style={{ marginTop: '30px', display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      className="btn btn-primary"
                      onClick={handlePrintQRCode}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <i className="fas fa-print"></i>
                      Imprimir QR Code
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={handleDownloadQRCode}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <i className="fas fa-download"></i>
                      Baixar Imagem
                    </button>
                    <button
                      className="btn btn-outline"
                      onClick={() => {
                        setQRCodeData(null);
                        loadQRCode();
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <i className="fas fa-sync-alt"></i>
                      Regenerar QR Code
                    </button>
                  </div>

                  {/* Informações adicionais */}
                  <div style={{
                    marginTop: '30px',
                    padding: '20px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    textAlign: 'left'
                  }}>
                    <h4 style={{ color: '#2c3e50', marginBottom: '15px' }}>
                      <i className="fas fa-info-circle" style={{ marginRight: '8px', color: '#04C64F' }}></i>
                      Como usar o QR Code
                    </h4>
                    <ul style={{ color: '#7f8c8d', lineHeight: '1.8', paddingLeft: '20px' }}>
                      <li>Imprima o QR Code e fixe em local visível no veículo</li>
                      <li>Durante o checklist, escaneie o QR Code para identificar automaticamente o veículo</li>
                      <li>O QR Code contém informações de identificação únicas do veículo</li>
                      <li>Recomendamos plastificar a impressão para maior durabilidade</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <i className="fas fa-exclamation-triangle" style={{ fontSize: '2rem', color: '#e74c3c' }}></i>
                  <p style={{ marginTop: '15px', color: '#7f8c8d' }}>Erro ao carregar QR Code</p>
                  <button
                    className="btn btn-primary"
                    onClick={loadQRCode}
                    style={{ marginTop: '15px' }}
                  >
                    Tentar novamente
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </ViewLayout>
  );
};

export default VehicleView;
